import { useSeo } from "@/lib/seo";

import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, Search, ShieldCheck, Zap, Users, Sparkles, Camera, Shirt, Smartphone, Bike, Disc3, Gamepad2, BookOpen, MapPin, WalletCards } from "lucide-react";
import { api } from "@/lib/api";
import ListingCard from "@/components/ListingCard";
import JustTradedRail from "@/components/JustTradedRail";
import { useAuth } from "@/contexts/AuthContext";
import { getCachedCoords, requestCoords } from "@/lib/geo";

const CATEGORIES = [
  { name: "Trading Cards", icon: WalletCards, color: "#B45309" },
  { name: "Electronics", icon: Smartphone, color: "#0654BA" },
  { name: "Photography", icon: Camera,     color: "#7C3AED" },
  { name: "Fashion",     icon: Shirt,      color: "#DB2777" },
  { name: "Outdoors",    icon: Bike,       color: "#059669" },
  { name: "Music",       icon: Disc3,      color: "#EA580C" },
  { name: "Gaming",      icon: Gamepad2,   color: "#0891B2" },
  { name: "Books",       icon: BookOpen,   color: "#7C2D12" },
];

const SUGGESTED = ["Pokémon cards", "Cameras", "Watches", "Bikes", "Vinyl", "Guitars", "Sneakers", "Gaming"];

const CARD_CHIPS = ["Pokémon", "Magic: The Gathering", "Yu-Gi-Oh!", "Baseball", "Basketball", "Football", "One Piece Card Game", "Disney Lorcana"];

export default function Home() {
  const [q, setQ] = useState("");
  const [trending, setTrending] = useState([]);
  const [stats, setStats] = useState(null);
  const [hasCoords, setHasCoords] = useState(false);
  const [enablingLocation, setEnablingLocation] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  useSeo({
    title: "Vyrti - A Trade-Only Marketplace",
    description: "Vyrti is a trade-only marketplace with no money involved. Swap cameras, phones, fashion, vinyl, gaming gear and more with trusted traders near you.",
    path: "/",
  });

  useEffect(() => {
    api.get("/listings", { params: { sort: "new", limit: 12 } }).then(({ data }) => setTrending(data.items || [])).catch(() => {});
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
    setHasCoords(!!getCachedCoords());
  }, []);

  const handleSearch = (e) => {
    e?.preventDefault?.();
    navigate(q.trim() ? `/browse?q=${encodeURIComponent(q.trim())}` : "/browse");
  };

  const enableLocation = async () => {
    setEnablingLocation(true);
    const coords = await requestCoords();
    setEnablingLocation(false);
    if (coords) setHasCoords(true);
  };

  return (
    <div>
      {/* ============== HERO — compact, value-led ============== */}
      <section className="bg-gradient-to-br from-[#0654BA] via-[#0445A0] to-[#063480] text-white" data-testid="hero-section">
        <div className="section-pad pt-6 pb-7 md:pt-14 md:pb-16">
          <div className="max-w-3xl">
            <p className="text-[10px] md:text-[11px] tracking-widest uppercase font-bold text-[#F5AF02] mb-2 md:mb-4">Trade-only marketplace · No cash</p>
            <h1 className="font-display text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
              Turn unused gear into <span className="text-[#F5AF02]">something you&apos;ll actually use.</span>
            </h1>
            <p className="hidden md:block mt-4 text-blue-100 text-base md:text-lg max-w-xl">
              Swap directly with verified traders nearby. Zero cash.
            </p>

            {/* Big search bar */}
            <form onSubmit={handleSearch} className="mt-4 md:mt-7 max-w-2xl flex items-stretch bg-white rounded-md overflow-hidden shadow-lg" data-testid="hero-search-form">
              <Search className="w-5 h-5 text-slate-400 ml-3 md:ml-4 self-center flex-shrink-0" />
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search 25,000+ items"
                className="flex-1 px-3 py-2.5 md:py-3.5 text-slate-900 outline-none placeholder:text-slate-400 text-sm md:text-base"
                data-testid="hero-search-input"
              />
              <button type="submit" className="bg-[#F5AF02] hover:bg-[#D89500] text-slate-900 font-bold px-4 md:px-8 transition-colors text-sm md:text-base whitespace-nowrap" data-testid="hero-search-button">
                Search
              </button>
            </form>

            {/* Suggested chips */}
            <div className="mt-3 md:mt-4 -mx-1 px-1 flex gap-2 overflow-x-auto no-scrollbar" data-testid="search-suggestions">
              <span className="text-[10px] md:text-[11px] uppercase tracking-widest text-blue-100/90 font-semibold flex-shrink-0 self-center mr-1">Try:</span>
              {SUGGESTED.map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => navigate(`/browse?q=${encodeURIComponent(s)}`)}
                  className="px-3 py-1 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 text-white text-[12px] font-medium whitespace-nowrap transition-colors flex-shrink-0"
                  data-testid={`suggested-${s.toLowerCase()}`}
                >
                  {s}
                </button>
              ))}
            </div>

            {/* Activity ribbon — only rendered once real stats have loaded */}
            {stats && (
            <div className="mt-4 md:mt-6 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] md:text-xs text-blue-100">
              <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#16A34A] animate-pulse" /> {stats.new_listings_week} new this week</span>
              <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5"/> {stats.new_members_week} new members</span>
              <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5"/> {stats.verified_traders} verified traders</span>
            </div>
            )}
          </div>
        </div>
      </section>

      {/* ============== JUST TRADED RAIL — social proof ============== */}
      <JustTradedRail />

      {/* ============== SWIPE CTA — mobile-first discovery ============== */}
      {user && (
        <section className="section-pad pt-4 pb-1 md:pt-6">
          <Link
            to="/discover"
            className="vyrti-card-raised flex items-center gap-4 p-4 md:p-5 group hover:border-[#0654BA] transition-colors"
            data-testid="home-swipe-cta"
          >
            <div className="w-11 h-11 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-[#0654BA] to-[#0445A0] text-white flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 md:w-6 md:h-6 fill-current" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display font-bold text-slate-900 text-sm md:text-base group-hover:text-[#0654BA] transition-colors">Discover by swipe</p>
              <p className="text-xs md:text-sm text-slate-500">Swipe right on items you&apos;d trade for. Owners see your interest.</p>
            </div>
            <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-[#0654BA] transition-colors" />
          </Link>
        </section>
      )}

      {/* ============== LIVE LISTINGS — surface inventory immediately ============== */}
      <section className="section-pad py-6 md:py-12" data-testid="live-trades-section">
        <div className="flex items-end justify-between mb-4 md:mb-5 flex-wrap gap-3">
          <div>
            <p className="eyebrow text-slate-500 mb-1.5 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[#E53238] animate-pulse" />
              Live now
            </p>
            <h2 className="font-display text-xl md:text-3xl font-bold text-slate-900">Newly listed trades</h2>
          </div>
          <Link to="/browse" className="tx-link text-sm inline-flex items-center gap-1" data-testid="home-browse-all">
            See all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Optional location nudge — only if user hasn't granted yet */}
        {user && !hasCoords && (
          <button
            type="button"
            onClick={enableLocation}
            className="mb-4 md:mb-5 w-full md:w-auto inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-md bg-[#E0EDFA] border border-[#BFDBFE] text-[#0654BA] text-xs md:text-sm font-semibold hover:bg-[#D0E2F5] transition-colors"
            data-testid="enable-location-button"
            disabled={enablingLocation}
          >
            <MapPin className="w-4 h-4" />
            {enablingLocation ? "Locating…" : "Show distance to each item"}
          </button>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
          {trending.map(l => <ListingCard key={l.id} listing={l} />)}
          {trending.length === 0 && Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="vyrti-card overflow-hidden">
              <div className="aspect-[4/3] skeleton" />
              <div className="p-3 space-y-2">
                <div className="skeleton h-3.5 w-3/4" />
                <div className="skeleton h-3 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============== CATEGORIES — tile grid ============== */}
      <section className="section-pad py-8 md:py-14 border-t border-slate-200" data-testid="categories-section">
        <div className="flex items-end justify-between mb-5 md:mb-6">
          <div>
            <p className="eyebrow text-slate-500 mb-1.5">Shop by category</p>
            <h2 className="font-display text-xl md:text-3xl font-bold text-slate-900">Browse categories</h2>
          </div>
          <Link to="/categories" className="tx-link text-sm hidden md:inline-flex items-center gap-1" data-testid="home-all-categories">
            All categories <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-4 md:grid-cols-8 gap-3 md:gap-4">
          {CATEGORIES.map(c => {
            const Icon = c.icon;
            return (
              <Link
                key={c.name}
                to={`/browse?category=${encodeURIComponent(c.name)}`}
                className="cat-tile p-3 md:p-4 flex flex-col items-center justify-center gap-2 group"
                data-testid={`cat-tile-${c.name.toLowerCase()}`}
              >
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-colors" style={{ background: `${c.color}15` }}>
                  <Icon className="w-5 h-5 md:w-6 md:h-6" style={{ color: c.color }} strokeWidth={2} />
                </div>
                <span className="text-[11px] md:text-xs font-semibold text-slate-700 text-center leading-tight">{c.name}</span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ============== TRADING CARDS SPOTLIGHT ============== */}
      <section className="section-pad pb-8 md:pb-14" data-testid="trading-cards-banner">
        <div className="relative overflow-hidden rounded-lg bg-slate-900 text-white">
          <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-[#F5AF02]/10 blur-2xl pointer-events-none" />
          <div className="absolute -left-10 -bottom-20 w-72 h-72 rounded-full bg-[#0654BA]/20 blur-2xl pointer-events-none" />
          <div className="relative p-6 md:p-10 flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
            <div className="flex-1">
              <p className="eyebrow text-[#F5AF02] mb-2 flex items-center gap-2">
                <WalletCards className="w-4 h-4" /> New on Vyrti
              </p>
              <h2 className="font-display text-2xl md:text-4xl font-bold leading-tight !text-white">
                Trade your trading cards.<br className="hidden md:block" /> Slabs, singles & sealed.
              </h2>
              <p className="text-slate-300 text-sm md:text-base mt-3 max-w-xl">
                Pokémon, Magic, Yu-Gi-Oh!, sports cards and more — tag your cards with grade (PSA, BGS, CGC, SGC), set and year, and swap with collectors near you. No cash, just trades.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {CARD_CHIPS.map(g => (
                  <Link
                    key={g}
                    to={`/browse?category=${encodeURIComponent("Trading Cards")}&card_game=${encodeURIComponent(g)}`}
                    className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-[#F5AF02] hover:text-slate-900 border border-white/15 text-xs font-semibold transition-colors"
                    data-testid={`card-chip-${g.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    {g}
                  </Link>
                ))}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row md:flex-col gap-3 flex-shrink-0">
              <Link to={`/browse?category=${encodeURIComponent("Trading Cards")}`} className="btn-block btn-accent !text-sm text-center" data-testid="cards-browse-cta">
                Browse card trades <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/post" className="btn-block !text-sm text-center bg-white/10 hover:bg-white/20 text-white border border-white/20 transition-colors" data-testid="cards-post-cta">
                List your cards
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============== STATS RIBBON — credibility metrics ============== */}
      {stats && (
      <section className="section-pad py-10 bg-slate-100 border-y border-slate-200">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-5xl mx-auto text-center">
          <div className="px-2">
            <p className="stat-num text-3xl md:text-5xl text-[#0654BA]">{stats.items_available.toLocaleString()}</p>
            <p className="text-[11px] md:text-sm tracking-wide text-slate-600 mt-2 font-medium">Items available</p>
          </div>
          <div className="px-2 md:border-l md:border-slate-200">
            <p className="stat-num text-3xl md:text-5xl text-[#0654BA]">{stats.new_listings_week.toLocaleString()}</p>
            <p className="text-[11px] md:text-sm tracking-wide text-slate-600 mt-2 font-medium">New this week</p>
          </div>
          <div className="px-2 md:border-l md:border-slate-200">
            <p className="stat-num text-3xl md:text-5xl text-[#0654BA]">{stats.verified_traders.toLocaleString()}</p>
            <p className="text-[11px] md:text-sm tracking-wide text-slate-600 mt-2 font-medium">Verified traders</p>
          </div>
          <div className="px-2 md:border-l md:border-slate-200">
            <p className="stat-num text-3xl md:text-5xl text-[#0654BA]">{stats.new_members_week.toLocaleString()}</p>
            <p className="text-[11px] md:text-sm tracking-wide text-slate-600 mt-2 font-medium">New members</p>
          </div>
        </div>
      </section>
      )}

      {/* ============== HOW IT WORKS — illustrative 3-step ============== */}
      <section className="section-pad py-12 md:py-16" data-testid="how-it-works">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="eyebrow text-slate-500 mb-2">How Vyrti works</p>
          <h2 className="font-display text-2xl md:text-4xl font-bold text-slate-900">Three steps, zero cash.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4 md:gap-6 max-w-5xl mx-auto relative">
          {[
            { n: "1", t: "List your item", d: "Snap a photo. Our AI identifies it and privately calibrates its trade range.", icon: Camera },
            { n: "2", t: "See what it unlocks", d: "Your Match Radar surfaces fair swaps, trade-ups, and bundles automatically.", icon: Zap },
            { n: "3", t: "Make a trade",     d: "Chat in-app, propose, accept. Meet locally or ship — your call.", icon: Sparkles },
          ].map((s, i, arr) => {
            const Icon = s.icon;
            return (
              <div key={s.n} className="vyrti-card p-5 md:p-6 relative">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-md bg-[#E0EDFA] text-[#0654BA] flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className="text-[11px] font-bold tracking-wider uppercase text-slate-400">Step {s.n}</span>
                </div>
                <h3 className="font-display font-bold text-lg text-slate-900 mb-1.5">{s.t}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.d}</p>
                {/* Connector arrow on desktop */}
                {i < arr.length - 1 && (
                  <ArrowRight className="hidden md:block w-5 h-5 text-slate-300 absolute -right-5 top-1/2 -translate-y-1/2 z-10" strokeWidth={2.5} />
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ============== CTA BANNER ============== */}
      <section className="section-pad py-8 md:py-10">
        <div className="bg-[#F5AF02] rounded-lg overflow-hidden relative">
          <div className="px-6 py-9 md:px-12 md:py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative z-10">
            <div className="max-w-2xl">
              <p className="text-[11px] tracking-widest uppercase font-bold text-slate-900/70 mb-2">Start trading</p>
              <h3 className="font-display text-2xl md:text-4xl font-bold text-slate-900 leading-tight">
                That thing in your closet? Someone else&apos;s treasure.
              </h3>
              <p className="text-slate-800/80 mt-2 text-sm md:text-base">List your first item in 2 mins for FREE.</p>
            </div>
            <Link to="/post" className="btn-block bg-slate-900 hover:bg-slate-800 text-white !px-7 !py-3.5 !text-base whitespace-nowrap" data-testid="home-cta-post">
              List an item
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============== Footer ============== */}
      <footer className="section-pad py-10 border-t border-slate-200 mt-4 text-sm text-slate-500 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <span className="font-display font-bold text-slate-900 text-lg leading-none">Vyrti</span>
          <span className="ml-2">© 2026</span>
        </div>
        <div className="flex items-center gap-5 flex-wrap">
          <Link to="/safety" className="hover:text-slate-900" data-testid="footer-safety">Safety</Link>
          <Link to="/categories" className="hover:text-slate-900">Categories</Link>
          <Link to="/terms" className="hover:text-slate-900" data-testid="footer-terms">Terms</Link>
          <Link to="/privacy" className="hover:text-slate-900" data-testid="footer-privacy">Privacy</Link>
          <a href="mailto:support@vyrti.com" className="hover:text-slate-900" data-testid="footer-contact">Contact</a>
        </div>
      </footer>
    </div>
  );
}
